import {
  documentListItemSchema,
  type DocumentListItem,
  type DocumentListResponse,
} from '@okrapdf/protocol/dashboard-http';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { Data, Effect, Schedule } from 'effect';
import { capture } from '@/lib/posthog';
import { okraApi } from '../okraApi';
import type { DocumentListQuery, RenderDocumentsPagesResponse } from '../types';

const DOCUMENT_PAGE_SIZE = 100;
const FIRST_PAGE_RETRY_DELAY = '400 millis';

export type CompleteDocumentListQuery = Omit<DocumentListQuery, 'after' | 'before' | 'limit'> & {
  pageSize?: number;
};

export class DocumentListBoundaryError extends Data.TaggedError('DocumentListBoundaryError')<{
  readonly operation: 'list-url' | 'complete-url' | 'parse-page' | 'paginate';
}> {}

export { DocumentListBoundaryError as DocumentListError };

const documentListCauses = new WeakMap<DocumentListBoundaryError, unknown>();

function documentListFailure(
  operation: DocumentListBoundaryError['operation'],
  cause: unknown,
): DocumentListBoundaryError {
  const error = new DocumentListBoundaryError({ operation });
  documentListCauses.set(error, cause);
  return error;
}

export function documentListErrorCause(error: DocumentListBoundaryError): unknown {
  return documentListCauses.get(error);
}

export const documentListUrlEffect = Effect.fn('web.api.documents.listUrl')(function* (
  params: DocumentListQuery | void,
) {
  return yield* Effect.try({
    try: () => {
      const search = new URLSearchParams();
      const limit = params?.limit ?? 50;
      if (limit) search.set('limit', String(limit));
      if (params?.q?.trim()) search.set('q', params.q.trim());
      if (params?.after) search.set('after', params.after);
      if (params?.before) search.set('before', params.before);
      if (params?.order) search.set('order', params.order);
      if (params?.hydrate === false) search.set('hydrate', '0');
      const query = search.toString();
      return `/v1/documents${query ? `?${query}` : ''}`;
    },
    catch: (cause) => documentListFailure('list-url', cause),
  });
});

function documentListUrl(params: DocumentListQuery | void): string {
  return Effect.runSync(documentListUrlEffect(params).pipe(Effect.orDie));
}

export const completeDocumentListUrlEffect = Effect.fn('web.api.documents.completeUrl')(function* (
  params: CompleteDocumentListQuery | void,
  cursor?: string,
) {
  return yield* Effect.try({
    try: () => {
      const search = new URLSearchParams();
      search.set(
        'limit',
        String(Math.min(DOCUMENT_PAGE_SIZE, Math.max(1, params?.pageSize ?? DOCUMENT_PAGE_SIZE))),
      );
      search.set('sort', 'created_at');
      if (params?.q?.trim()) search.set('q', params.q.trim());
      if (params?.order) search.set('order', params.order);
      if (params?.hydrate === false) search.set('hydrate', '0');
      if (cursor) search.set('cursor', cursor);
      return `/v1/documents?${search.toString()}`;
    },
    catch: (cause) => documentListFailure('complete-url', cause),
  });
});

function invalidDocumentListError(message: string, data?: unknown): FetchBaseQueryError {
  return { status: 'CUSTOM_ERROR', error: message, data };
}

export const parseDocumentListPageEffect = Effect.fn('web.api.documents.parsePage')(function* (
  value: unknown,
): Effect.fn.Return<
  { data: DocumentListItem[]; hasMore: boolean; nextCursor: string | null },
  DocumentListBoundaryError
> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return yield* Effect.fail(
      documentListFailure(
        'parse-page',
        invalidDocumentListError('The document list returned an invalid response.', value),
      ),
    );
  }
  const envelope = value as {
    data?: unknown;
    documents?: unknown;
    has_more?: unknown;
    next_cursor?: unknown;
  };
  const rows = Array.isArray(envelope.data) ? envelope.data : envelope.documents;
  if (!Array.isArray(rows)) {
    return yield* Effect.fail(
      documentListFailure(
        'parse-page',
        invalidDocumentListError('The document list response is missing its data array.', value),
      ),
    );
  }
  if (typeof envelope.has_more !== 'boolean') {
    return yield* Effect.fail(
      documentListFailure(
        'parse-page',
        invalidDocumentListError(
          'The document list response is missing boolean pagination metadata.',
          value,
        ),
      ),
    );
  }
  const parsed = documentListItemSchema.array().safeParse(rows);
  if (!parsed.success) {
    return yield* Effect.fail(
      documentListFailure(
        'parse-page',
        invalidDocumentListError(
          'The document list contains an invalid record.',
          parsed.error.flatten(),
        ),
      ),
    );
  }
  if (
    envelope.next_cursor !== undefined &&
    envelope.next_cursor !== null &&
    typeof envelope.next_cursor !== 'string'
  ) {
    return yield* Effect.fail(
      documentListFailure(
        'parse-page',
        invalidDocumentListError('The document list returned an invalid cursor.', value),
      ),
    );
  }
  return {
    data: parsed.data,
    hasMore: envelope.has_more,
    nextCursor: envelope.next_cursor ?? null,
  };
});

type DocumentListFetchPageResult = {
  readonly data?: unknown;
  readonly error?: FetchBaseQueryError;
};

export type DocumentInventoryPage = {
  data: DocumentListItem[];
  hasMore: boolean;
  nextCursor: string | null;
};

function isServerDocumentListFailure(failure: DocumentListBoundaryError): boolean {
  const status = (documentListErrorCause(failure) as FetchBaseQueryError | undefined)?.status;
  return typeof status === 'number' && status >= 500;
}

/** `document_list_failed`: which inventory page failed and how; no document data. */
function reportDocumentListFailed(error: FetchBaseQueryError | undefined, cursor: string | null) {
  try {
    capture('document_list_failed', {
      surface: 'documents',
      status: error?.status ?? null,
      page: cursor ? 'next' : 'first',
    });
  } catch {
    // Telemetry never changes the query result.
  }
}

/** One immutable `created_at` keyset page of the owner's document inventory. */
export const fetchDocumentInventoryPageEffect = Effect.fn('web.api.documents.fetchPage')(
  function* (
    params: CompleteDocumentListQuery | void,
    cursor: string | null | undefined,
    fetchPage: (url: string) => PromiseLike<DocumentListFetchPageResult>,
  ): Effect.fn.Return<DocumentInventoryPage, DocumentListBoundaryError> {
    const url = yield* completeDocumentListUrlEffect(params, cursor ?? undefined);
    const result = yield* Effect.tryPromise({
      try: () => Promise.resolve(fetchPage(url)),
      catch: (cause) => documentListFailure('paginate', cause),
    });
    if (result.error) {
      return yield* Effect.fail(documentListFailure('paginate', result.error));
    }
    return yield* parseDocumentListPageEffect(result.data);
  },
);

/**
 * Concatenate loaded inventory pages in order. The `created_at` keyset never
 * repeats a row, so this only guards against a stale cache page after a
 * cursor-less refetch.
 */
export function flattenDocumentInventory(
  pages: readonly DocumentInventoryPage[],
): DocumentListItem[] {
  const seen = new Set<string>();
  const documents: DocumentListItem[] = [];
  for (const page of pages) {
    for (const document of page.data) {
      if (seen.has(document.id)) continue;
      seen.add(document.id);
      documents.push(document);
    }
  }
  return documents;
}

export const listAllDocumentsEffect = Effect.fn('web.api.documents.listAll')(function* (
  params: CompleteDocumentListQuery | void,
  fetchPage: (url: string) => PromiseLike<DocumentListFetchPageResult>,
): Effect.fn.Return<DocumentListItem[], DocumentListBoundaryError> {
  const documents: DocumentListItem[] = [];
  const seenIds = new Set<string>();
  let cursor: string | undefined;

  while (true) {
    const page = yield* fetchDocumentInventoryPageEffect(params, cursor, fetchPage);

    for (const document of page.data) {
      if (seenIds.has(document.id)) {
        return yield* Effect.fail(
          documentListFailure(
            'paginate',
            invalidDocumentListError(
              'Document pagination repeated a record, so the inventory may be incomplete.',
              { document_id: document.id },
            ),
          ),
        );
      }
      seenIds.add(document.id);
      documents.push(document);
    }

    if (!page.hasMore) return documents;
    if (!page.nextCursor || page.nextCursor === cursor) {
      return yield* Effect.fail(
        documentListFailure(
          'paginate',
          invalidDocumentListError('Document pagination did not return a usable next cursor.'),
        ),
      );
    }
    cursor = page.nextCursor;
  }
});

export const documentsEndpoints = okraApi.injectEndpoints({
  endpoints: (build) => ({
    listDocuments: build.query<DocumentListResponse, DocumentListQuery | void>({
      query: (params) => documentListUrl(params),
      providesTags: ['Documents'],
    }),
    /**
     * Cursor-paginated inventory for the Documents page: the first page paints
     * immediately and `fetchNextPage` follows `next_cursor`. Prefer this over
     * `listAllDocuments`, which walks every page before it resolves (a
     * 2.8k-document workspace took 29 serial requests, ~12 s, before render).
     */
    documentsInventory: build.infiniteQuery<
      DocumentInventoryPage,
      CompleteDocumentListQuery,
      string | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: (lastPage) =>
          lastPage.hasMore && lastPage.nextCursor ? lastPage.nextCursor : undefined,
        // An invalidation (an upload, a delete) reloads page one only. Reloading every
        // cached page in series turned one failed page into a failed list.
        refetchCachedPages: false,
      },
      async queryFn({ queryArg, pageParam }, _api, _extraOptions, fetchWithBQ) {
        const page = fetchDocumentInventoryPageEffect(queryArg, pageParam, (url) =>
          Promise.resolve(fetchWithBQ(url)),
        );
        // A first page with no cached rows shows a full error screen, so a
        // transient 5xx gets one more try. Later pages keep their Retry control.
        const result = await Effect.runPromise(
          Effect.result(
            pageParam === null
              ? page.pipe(
                  Effect.retry({
                    times: 1,
                    schedule: Schedule.spaced(FIRST_PAGE_RETRY_DELAY),
                    while: isServerDocumentListFailure,
                  }),
                )
              : page,
          ),
        );
        if (result._tag === 'Failure') {
          const error = documentListErrorCause(result.failure) as FetchBaseQueryError;
          reportDocumentListFailed(error, pageParam);
          return { error };
        }
        return { data: result.success };
      },
      providesTags: ['Documents'],
    }),
    listAllDocuments: build.query<DocumentListResponse, CompleteDocumentListQuery | void>({
      async queryFn(params, _api, _extraOptions, fetchWithBQ) {
        const result = await Effect.runPromise(
          Effect.result(listAllDocumentsEffect(params, (url) => Promise.resolve(fetchWithBQ(url)))),
        );
        if (result._tag === 'Failure') {
          return { error: documentListErrorCause(result.failure) as FetchBaseQueryError };
        }
        const documents = result.success;

        return {
          data: {
            data: documents,
            documents,
            has_more: false,
          },
        };
      },
      providesTags: ['Documents'],
    }),
    renderDocumentPages: build.mutation<
      { message?: string; phase?: string; rendered?: number },
      { documentId: string; direct?: boolean }
    >({
      query: ({ documentId, direct }) => ({
        url: `/v1/documents/${encodeURIComponent(documentId)}${direct ? '/render-pages?direct=1' : '/render-pages'}`,
        method: 'POST',
      }),
      invalidatesTags: ['Documents'],
    }),
    renderDocumentsPages: build.mutation<
      RenderDocumentsPagesResponse,
      { documentIds?: string[]; limit?: number } | void
    >({
      query: (body) => ({
        url: '/v1/documents/render-pages',
        method: 'POST',
        body: body
          ? {
              document_ids: body.documentIds,
              limit: body.limit,
            }
          : { limit: 200 },
      }),
      invalidatesTags: ['Documents'],
    }),
  }),
});

export const {
  useListDocumentsQuery,
  useDocumentsInventoryInfiniteQuery,
  useListAllDocumentsQuery,
  useRenderDocumentPagesMutation,
  useRenderDocumentsPagesMutation,
} = documentsEndpoints;
